import { useState, useEffect, useRef } from 'react';
import { useAppSelector } from '@/state/store';
import { usePostInstrumentation } from '@/components/smartai/api/postInstrumentation';
import { usePostGithubInstrumentation } from '@/components/smartai/api/postGithubInstrumentation';
import {
  useCheckPRStatus,
  useCheckBuildStatus,
  type JobStatusResponse,
} from '@/components/smartai/api/getJobStatus';
import type { FileDetail } from './OnboardInventoryContent';
import OnboardInventoryContent from './OnboardInventoryContent';
import { getApiErrorMessage } from '@/utils';

/** Normalize repo URLs so same-repo app/test selections compare equal. */
const normalizeRepoUrl = (url: string) =>
  url
    .trim()
    .replace(/\.git$/i, '')
    .replace(/\/+$/, '')
    .toLowerCase();

const OnboardInventoryView = () => {
  const {
    scmProvider,
    scanId,
    scanResult,
    prEmails,
    autoPrEmails,
    patToken,
    org,
    azureProject,
    pipelineName,
    repoUrl,
    branchName,
    testPatToken,
    testOrg,
    testProject,
    testPipelineName,
    testRepoUrl,
    testBranchName,
    testPipelinePlatform,
    repoId,
    repoName,
    files,
  } = useAppSelector(state => state.onboarding);

  const isGithub = scmProvider === 'github';
  const instrumentationStarted = useRef(false);

  const mainAdoMutation = usePostInstrumentation(patToken);
  const testAdoMutation = usePostInstrumentation(testPatToken);
  const mainGithubMutation = usePostGithubInstrumentation(patToken);
  const testGithubMutation = usePostGithubInstrumentation(testPatToken);

  const mainMutation = isGithub ? mainGithubMutation : mainAdoMutation;
  const testMutation = isGithub ? testGithubMutation : testAdoMutation;

  const [notification, setNotification] = useState<{
    message: string;
    kind: 'success' | 'error';
  } | null>(null);

  useEffect(() => {
    if (!patToken || !prEmails.length) return;
    if (instrumentationStarted.current) return;
    instrumentationStarted.current = true;

    const sameTestRepo =
      !!testRepoUrl?.trim() &&
      normalizeRepoUrl(testRepoUrl) === normalizeRepoUrl(repoUrl);

    if (isGithub) {
      mainGithubMutation.mutate({
        github_org: org,
        repo_url: repoUrl,
        target_branch: branchName.replace(/^refs\/heads\//, ''),
        reviewers: prEmails,
        repo_id: repoId || undefined,
        instrumentation_target: 'app',
      });
      // Separate scripts repo only — same monorepo is already covered by the app job
      if (testRepoUrl?.trim() && testPatToken && !sameTestRepo) {
        testGithubMutation.mutate({
          github_org: testOrg || org,
          repo_url: testRepoUrl,
          target_branch: (testBranchName || branchName).replace(
            /^refs\/heads\//,
            '',
          ),
          reviewers: autoPrEmails?.length ? autoPrEmails : prEmails,
          repo_id: repoId || undefined,
          instrumentation_target: 'test',
        });
      }
      return;
    }

    mainAdoMutation.mutate({
      azure_org: org,
      azure_project: azureProject,
      pipeline_name: pipelineName,
      repo_url: repoUrl,
      target_branch: branchName.replace(/^refs\/heads\//, ''),
      reviewers: prEmails,
      backend_project: '',
      repo_id: repoId || undefined,
      instrumentation_target: 'app',
    });

    if (testRepoUrl?.trim() && !sameTestRepo) {
      testAdoMutation.mutate({
        azure_org: testOrg,
        azure_project: testProject,
        pipeline_name: testPipelineName,
        repo_url: testRepoUrl,
        target_branch: testBranchName.replace(/^refs\/heads\//, ''),
        reviewers: autoPrEmails,
        backend_project: azureProject,
        repo_id: repoId || undefined,
        instrumentation_target: 'test',
      });
    }
    // Re-run when e2e seeds (or Start Full Scan) populate PAT + emails.
  }, [patToken, prEmails.length, testPatToken, autoPrEmails.length]);

  const mainPRMutation = useCheckPRStatus();
  const mainBuildMutation = useCheckBuildStatus();
  const autoPRMutation = useCheckPRStatus();
  const autoBuildMutation = useCheckBuildStatus();

  const showNotification = (message: string, kind: 'success' | 'error') => {
    setNotification({ message, kind });
    setTimeout(() => setNotification(null), 4000);
  };

  const fileDetails = (
    (scanResult?.astAnalysis?.fileDetails as FileDetail[] | undefined) ?? []
  ).map(f => ({ ...f }));

  return (
    <OnboardInventoryContent
      org={org}
      azureProject={azureProject}
      repoName={repoName}
      branchName={branchName}
      prEmails={prEmails}
      autoPrEmails={autoPrEmails}
      mainJobId={mainMutation.data?.job_id}
      testJobId={testMutation.data?.job_id}
      onCheckMainPR={() =>
        mainPRMutation.mutate(mainMutation.data!.job_id, {
          onSuccess: (d: JobStatusResponse) =>
            showNotification(d?.message ?? d?.title ?? 'Success', 'success'),
          onError: (err: Error) =>
            showNotification(
              getApiErrorMessage(err, 'Request Faield'),
              'error',
            ),
        })
      }
      onCheckMainBuild={() =>
        mainBuildMutation.mutate(mainMutation.data!.job_id, {
          onSuccess: (d: JobStatusResponse) =>
            showNotification(d?.message ?? 'Success', 'success'),
          onError: (err: Error) =>
            showNotification(
              getApiErrorMessage(err, 'Request Faield'),
              'error',
            ),
        })
      }
      onCheckAutoPR={() =>
        autoPRMutation.mutate(testMutation.data!.job_id, {
          onSuccess: (d: JobStatusResponse) =>
            showNotification(d?.message ?? d?.title ?? 'Success', 'success'),
          onError: (err: Error) =>
            showNotification(
              getApiErrorMessage(err, 'Request Faield'),
              'error',
            ),
        })
      }
      onCheckAutoBuild={() =>
        autoBuildMutation.mutate(testMutation.data!.job_id, {
          onSuccess: (d: JobStatusResponse) =>
            showNotification(d?.message ?? 'Success', 'success'),
          onError: (err: Error) =>
            showNotification(
              getApiErrorMessage(err, 'Request Faield'),
              'error',
            ),
        })
      }
      notification={notification}
      scanResult={scanResult}
      fileDetails={fileDetails}
      selectedCount={files.length}
      repoUrl={repoUrl}
      scanId={scanId}
      pat={patToken}
    />
  );
};

export default OnboardInventoryView;
